import { FormikContextType, useFormik } from "formik";
import * as _ from "lodash";
import { keyBy } from "lodash";
import { useSnackbar } from "notistack";
import {
  ReactElement,
  useContext,
  createContext,
  ReactNode,
  useRef,
} from "react";
import * as yup from "yup";

import { ALL_AREA_CODES } from "@stw/states";
import {
  tenDlcStaffBrandRegistrationSchema,
  tenDlcStaffCampaignRegistrationSchema,
} from "@stw/yup-validators";

import {
  GetRegistrationForStaffQuery,
  ImportTcrBrandToRegistrationMutation,
  ImportTcrBrandToRegistrationMutationVariables,
  ProviderName,
  TenDlcExpectedTexts,
  TenDlcOrgType,
  UpdateBrandRegistrationForStaffMutation,
  UpdateBrandRegistrationForStaffMutationVariables,
  UpdateCampaignRegistrationForStaffMutation,
  UpdateCampaignRegistrationForStaffMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { LIST_COMPLIANCE_BUNDLES } from "~frontend/graphql/complianceBundles";
import {
  GET_REGISTRATION_FOR_STAFF,
  IMPORT_TCR_BRAND_TO_REGISTRATION,
  LIST_ALL_TEN_DLC_CAMPAIGN_REGISTRATIONS,
  UPDATE_TENDLC_BRAND_REGISTRATION_FOR_STAFF,
  UPDATE_TENDLC_CAMPAIGN_REGISTRATION_FOR_STAFF,
} from "~frontend/graphql/tenDlcRegistrations";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import useOptOutPhraseList from "~frontend/routes/subaccounts/campaignEditor/Script/useOptOutPhraseList";
import useMutation from "~frontend/util/useMutation";

import { BrandFormValues, CampaignFormValues } from "./types";

type BrandRegistration =
  GetRegistrationForStaffQuery["getRegistrationForStaff"];
type CampaignRegistration = BrandRegistration["campaignRegistrations"][number];

type TenDlcContextType = {
  brand: BrandRegistration;
  brandFormik: FormikContextType<BrandFormValues>;
  campaignFormiks: {
    [campaignRegistrationId: string]: FormikContextType<CampaignFormValues>;
  };
  handleUpdateBrand: () => Promise<void>;
  handleUpdateCampaign: (campaignRegistrationId: string) => Promise<void>;
  handleUpdateAll: () => Promise<void>;
  handleResetAll: () => void;
  handleImportExistingBrand: (brandId: string) => Promise<void>;
  isAnyFormModified: boolean;
  isAnyFormSubmitting: boolean;
};

const TenDlcContext = createContext<TenDlcContextType | null>(null);

export const useTenDlcContext = (): TenDlcContextType | null => {
  return useContext(TenDlcContext);
};

export const initialBrandValues = (
  brand: BrandRegistration
): BrandFormValues => ({
  tenantId: brand.tenantId || "",
  subaccountId: brand.subaccountId || "",
  tcrBrandId: brand.tcrBrandId || "",
  organizationName: brand.organizationName || "",
  displayName: brand.displayName || "",
  orgType: brand.orgType || "",
  ein: brand.ein || "",
  einIssuingCountry: brand.einIssuingCountry || "US",
  cvToken: brand.cvToken || "",
  focusArea: brand.focusArea || "",
  expectedTexts: brand.expectedTexts || ("" as TenDlcExpectedTexts),
  desiredAreaCodes:
    brand.desiredAreaCodes?.filter(ac => ALL_AREA_CODES.has(ac)) ||
    ([] as string[]),
  preferredAreaCode: brand.preferredAreaCode || "",
  addressOne: brand.addressOne || "",
  addressTwo: brand.addressTwo || "",
  city: brand.city || "",
  state: brand.state || "",
  postalCode: brand.postalCode || "",
  country: brand.country || "US",
  primaryContactName: brand.primaryContactName || "",
  primaryContactPhone: brand.primaryContactPhone || "",
  primaryContactEmail: brand.primaryContactEmail || "",
  organizationContactName: brand.organizationContactName || "",
  organizationContactPhone: brand.organizationContactPhone || "",
  organizationContactEmail: brand.organizationContactEmail || "",
  orgContactIsPrimaryContact: !brand.primaryContactName,
  website: brand.website || "",
  assignedToStaffEmail:
    "assignedToStaffEmail" in brand ? brand.assignedToStaffEmail || "" : "",
  validateRequiredFields: false,
});

export const initialCampaignValues = (
  campaign: CampaignRegistration
): CampaignFormValues => {
  const sampleScripts = campaign.sampleScripts || [];
  return {
    tenantId: campaign.tenantId,
    subaccountId: campaign.subaccountId,
    campaignRegistrationId: campaign.id,
    useCase: campaign.useCase || "",
    friendlyName: campaign.friendlyName || "",
    description: campaign.description || "",
    embeddedPhone: campaign.embeddedPhone ?? false,
    acknowledgedCompliance: campaign.acknowledgedCompliance,
    createComplianceBundle: campaign.createComplianceBundle ?? false,
    termsAndConditionsLink: campaign.termsAndConditionsLink || "",
    privacyPolicyLink: campaign.privacyPolicyLink || "",
    optInLink: campaign.optInLink || "",
    optInConfirmationMessage: campaign.optInConfirmationMessage || "",
    helpMessage: campaign.helpMessage || "",
    optoutMessage: campaign.optoutMessage || "",
    sampleScript1: sampleScripts[0] || "",
    sampleScript2: sampleScripts[1] || "",
    sampleScript3: sampleScripts[2] || "",
    sampleScript4: sampleScripts[3] || "",
    additionalContext: campaign.additionalContext || "",
    expediteStatus: campaign.expediteStatus,
    providerName: campaign.providerName || ProviderName.Bandwidth,
    messageFlowStaff: campaign.messageFlowStaff || "",
    assignedToStaffEmail: campaign.assignedToStaffEmail || "",
    validateRequiredFields: false,
    usageTypes: campaign.usageTypes || [],
  };
};

export const InputRow = ({
  children,
}: {
  children: ReactElement | (string | ReactElement)[];
}): ReactElement => (
  <FlexBox
    flexWrap="wrap"
    sx={{
      "> .MuiFormControl-root, > div": { minWidth: "48%", flexGrow: 1 },
      columnGap: 4,
    }}
  >
    {children}
  </FlexBox>
);

export const TenDlcProvider = ({
  brand,
  children,
}: {
  brand: BrandRegistration;
  children: ReactNode;
}): ReactElement => {
  const tenant = useTenant();
  const snackbar = useSnackbar();
  const [updateBrand] = useMutation<
    UpdateBrandRegistrationForStaffMutation,
    UpdateBrandRegistrationForStaffMutationVariables
  >(UPDATE_TENDLC_BRAND_REGISTRATION_FOR_STAFF, {
    onErrorSnackbarMessage: "Error updating brand registration",
    refetchQueries: [LIST_ALL_TEN_DLC_CAMPAIGN_REGISTRATIONS],
    awaitRefetchQueries: true,
  });

  const [updateCampaign] = useMutation<
    UpdateCampaignRegistrationForStaffMutation,
    UpdateCampaignRegistrationForStaffMutationVariables
  >(UPDATE_TENDLC_CAMPAIGN_REGISTRATION_FOR_STAFF, {
    onErrorSnackbarMessage: "Error updating campaign registration",
    refetchQueries: [
      LIST_ALL_TEN_DLC_CAMPAIGN_REGISTRATIONS,
      LIST_COMPLIANCE_BUNDLES,
    ],
    awaitRefetchQueries: true,
  });

  const { phraseList: optOutPhraseList } = useOptOutPhraseList();

  const brandFormik = useFormik({
    initialValues: initialBrandValues(brand),
    validationSchema: tenDlcStaffBrandRegistrationSchema(yup),
    onSubmit: async (
      {
        orgContactIsPrimaryContact,
        tcrBrandId: _tcrBrandId,
        validateRequiredFields: _validateRequiredFields,
        ...values
      },
      { resetForm }
    ) => {
      const { data, errors } = await updateBrand({
        variables: {
          input: {
            brandRegistrationId: brand.id,
            ...values,
            assignedToStaffEmail: values.assignedToStaffEmail || null,
            expectedTexts: values.expectedTexts as TenDlcExpectedTexts,
            orgType: values.orgType as TenDlcOrgType,
            website: values.website?.startsWith("http")
              ? values.website
              : `https://${values.website}`,
            cvToken:
              values.orgType === TenDlcOrgType.Political
                ? values.cvToken
                : undefined,
            focusArea:
              values.orgType === TenDlcOrgType.Private
                ? values.focusArea
                : undefined,
            ...(orgContactIsPrimaryContact
              ? {
                  primaryContactName: undefined,
                  primaryContactPhone: undefined,
                  primaryContactEmail: undefined,
                }
              : {}),
          },
        },
      });

      if (errors) {
        throw new Error(errors[0].message);
      }

      snackbar.enqueueSnackbar("Brand updated", { variant: "success" });
      const updatedBrand = data?.updateBrandRegistrationForStaff;
      if (updatedBrand) {
        resetForm({
          values: initialBrandValues(updatedBrand),
        });
      } else {
        throw new Error("No brand returned from update");
      }
    },
  });

  const [importExistingBrand] = useMutation<
    ImportTcrBrandToRegistrationMutation,
    ImportTcrBrandToRegistrationMutationVariables
  >(IMPORT_TCR_BRAND_TO_REGISTRATION, {
    refetchQueries: [
      GET_REGISTRATION_FOR_STAFF,
      LIST_ALL_TEN_DLC_CAMPAIGN_REGISTRATIONS,
    ],
    onErrorSnackbarMessage: "Error importing brand from TCR",
    awaitRefetchQueries: true,
  });

  const handleImportExistingBrand = async (brandId: string) => {
    const { data } = await importExistingBrand({
      variables: {
        input: {
          brandRegistrationId: brand.id,
          tcrBrandId: brandId,
        },
      },
    });
    if (data) {
      brandFormik.resetForm({
        values: initialBrandValues(data.importTcrBrandToRegistration),
      });
    }
  };

  const campaignRegistrations = [...brand.campaignRegistrations]
    .filter(campaignReg => !!campaignReg.submittedAt)
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

  const initialCampaignRegistrations = useRef(campaignRegistrations);
  if (
    initialCampaignRegistrations.current.map(c => c.id).join("|") !=
    campaignRegistrations.map(c => c.id).join("|")
  ) {
    throw new Error(
      "brand.campaignRegistrations changed; will violate rules of hooks"
    );
  }

  const campaignFormiks = campaignRegistrations.map(campaign => {
    // We can disable the rules-of-hooks here because we will always
    // create the same number of formik instances in the same order.

    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useFormik({
      enableReinitialize: true,
      initialValues: initialCampaignValues(campaign),
      validateOnMount: true,
      initialTouched: Object.keys(campaign).reduce((a, c) => {
        return { ...a, [c]: true };
      }, {}),
      validationSchema: tenDlcStaffCampaignRegistrationSchema(
        yup,
        optOutPhraseList || []
      ),
      onSubmit: async (
        {
          tenantId: _tenantId,
          subaccountId: _subaccountId,
          validateRequiredFields: _validateRequiredFields,
          ...values
        },
        { resetForm }
      ) => {
        const sampleScripts: string[] = [
          values.sampleScript1,
          values.sampleScript2,
        ];
        for (const sample of [values.sampleScript3, values.sampleScript4]) {
          if (sample !== null) {
            sampleScripts.push(sample);
          }
        }
        const { data, errors } = await updateCampaign({
          variables: {
            input: {
              ..._.omit(values, [
                "sampleScript1",
                "sampleScript2",
                "sampleScript3",
                "sampleScript4",
              ]),
              sampleScripts,
              tenantId: tenant!.id,
            },
          },
        });

        if (errors) {
          throw new Error(errors[0].message);
        }

        snackbar.enqueueSnackbar("Campaign updated", {
          variant: "success",
        });
        const updatedCampaign = data?.updateCampaignRegistrationForStaff;
        if (updatedCampaign) {
          resetForm({
            values: initialCampaignValues(updatedCampaign),
          });
        } else {
          throw new Error("No campaign returned from update");
        }
      },
    });
  });
  const campaignFormiksById = keyBy(
    campaignFormiks,
    formik => formik.values.campaignRegistrationId
  );

  const handleUpdateBrand = async () => {
    await brandFormik.submitForm();
  };

  const handleUpdateCampaign = async (campaignRegistrationId: string) => {
    const campaignFormik = campaignFormiksById[campaignRegistrationId];
    if (!campaignFormik) {
      snackbar.enqueueSnackbar("Campaign form not found", { variant: "error" });
      return;
    }
    if (!campaignFormik.dirty) {
      snackbar.enqueueSnackbar("No changes to save", { variant: "warning" });
      return;
    }
    await campaignFormiksById[campaignRegistrationId].submitForm();
  };

  const handleUpdateAll = async () => {
    await handleUpdateBrand();
    await Promise.all(
      campaignFormiks
        .filter(formik => formik.dirty)
        .map(formik => formik.submitForm())
    );
  };

  const handleResetAll = () => {
    brandFormik.resetForm();
    campaignFormiks.forEach(formik => formik.resetForm());
  };

  const isAnyFormModified =
    brandFormik.dirty ||
    Object.values(campaignFormiks).some(formik => formik.dirty);

  const isAnyFormSubmitting =
    brandFormik.isSubmitting ||
    Object.values(campaignFormiks).some(formik => formik.isSubmitting);

  return (
    <TenDlcContext.Provider
      value={{
        brand,
        brandFormik,
        campaignFormiks: campaignFormiksById,
        handleUpdateBrand,
        handleUpdateCampaign,
        handleUpdateAll,
        handleResetAll,
        handleImportExistingBrand,
        isAnyFormModified,
        isAnyFormSubmitting,
      }}
    >
      {children}
    </TenDlcContext.Provider>
  );
};
